import { lazy, Suspense, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useLanguage } from "../LanguageContext";
import { useUi } from "../UiContext";
import ContextMenu from "./ContextMenu";
import Footer from "./footer/Footer";
import Header from "./header/Header";
import { Notice, PrivacyDialog } from "./Privacy";

const CommandPalette = lazy(() => import("./CommandPalette"));

function CommandPaletteHost() {
	const { searchOpen } = useUi();
	const [load, setLoad] = useState(false);

	useEffect(() => {
		if (searchOpen) setLoad(true);
	}, [searchOpen]);

	if (!load) return null;

	return (
		<Suspense fallback={null}>
			<CommandPalette />
		</Suspense>
	);
}

function ScrollToTop() {
	const { pathname } = useLocation();
	useEffect(() => {
		window.scrollTo(0, 0);
	}, [pathname]);
	return null;
}

const SHARE_LOCALE: Record<string, string> = {
	en: "en_US",
	it: "it_IT",
	fr: "fr_FR",
};

function setMeta(selector: string, content: string) {
	const meta = document.head.querySelector(selector);
	if (meta) meta.setAttribute("content", content);
}

function DocumentTitle() {
	const { text, lang } = useLanguage();
	useEffect(() => {
		document.title = text.site.metaTitle;
		setMeta('meta[name="description"]', text.site.metaDescription);
		setMeta('meta[property="og:title"]', text.site.metaTitle);
		setMeta('meta[property="og:description"]', text.site.metaDescription);
		setMeta('meta[name="twitter:title"]', text.site.metaTitle);
		setMeta('meta[name="twitter:description"]', text.site.metaDescription);
		setMeta(
			'meta[property="og:locale"]',
			SHARE_LOCALE[lang] || SHARE_LOCALE.en,
		);
	}, [text, lang]);
	return null;
}

export default function SiteLayout() {
	const { pathname } = useLocation();
	const isLabRoute =
		pathname === "/lab" ||
		pathname === "/lab/" ||
		/^\/lab\/[^/]+\/?$/.test(pathname);
	const isBlogIndex = pathname === "/blog" || pathname === "/blog/";
	const hideFooter = isLabRoute || isBlogIndex;

	return (
		<div className={`site-shell${isLabRoute ? " site-shell--lab" : ""}`}>
			<ScrollToTop />
			<DocumentTitle />
			<Header />
			<main className="site-main">
				<Outlet />
			</main>
			{hideFooter ? null : <Footer />}
			<CommandPaletteHost />
			<PrivacyDialog />
			<Notice />
			<ContextMenu />
		</div>
	);
}
